import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { Button } from "../components/Button";
import { SettingsForm } from "./SettingsForm";
import { Dialog, DialogError } from "../components/Dialog";
import { DialogUpload } from "../components/DialogUpload";
import { RequestNotice } from "../components/RequestNotice";

import type { BusinessIdentity as Identity } from "../../shared/document-business-details";
import { countries } from "../components/countries";
import { Checkbox } from "../components/FormControls";

export function BusinessDetailsDialog({ legacyContactLines = [], identity, baseline, busy, error, kind = "invoice", scope = "document", profileAvailable = true, presentation = "dialog", status, onChange, onChooseLogo, onSubmit, onClose, restoreFocusRef }: {
  legacyContactLines?: string[];
  presentation?: "dialog" | "page";
  status?: React.ReactNode;
  identity: Identity;
  baseline: Identity | null;
  busy: boolean;
  error: string | null;
  kind?: "invoice" | "estimate";
  scope?: "document" | "profile";
  issued?: boolean;
  profileAvailable?: boolean;
  onChange(identity: Identity): void;
  onChooseLogo(file: File): Promise<boolean>;
  onSubmit(updateProfile: boolean): Promise<boolean>;
  onClose(): void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const id = useId();
  const [uploading, setUploading] = useState(false);
  const [legacyContactsReviewed, setLegacyContactsReviewed] = useState(false);
  const legacyContactKey = legacyContactLines.join("\n");
  useEffect(() => setLegacyContactsReviewed(false), [legacyContactKey]);
  const [updateProfile, setUpdateProfile] = useState(false);
  const [showAddressLine2, setShowAddressLine2] = useState(false);
  const line2Ref = useRef<HTMLInputElement>(null);
  const disabled = busy || uploading || (presentation === "page" && baseline === null);
  const isUnitedStates = identity.address.country === "US";
  const addressFields = [
    ["line1", "Address line 1", "address-line1", true, 1000],
    ["line2", "Address line 2 (optional)", "address-line2", false, 1000],
    ["locality", "City", "address-level2", true, 200],
    ["region", "State / region", "address-level1", true, 200],
    ["postalCode", "ZIP / postal code", "postal-code", true, 40],
  ] as const;

  useEffect(() => {
    if (showAddressLine2) line2Ref.current?.focus();
  }, [showAddressLine2]);

  const Container = presentation === "page" ? SettingsForm : Dialog;
  return <Container label="Business details"
    description={scope === "profile" ? "Update the business details reused for new invoices and estimates. Existing documents retain their own details." : <>Changes apply to this {kind}. Update your <a className="linkish" href="#" onClick={event => event.preventDefault()}>Business Profile</a> to reuse them later.</>}
    dirty={baseline !== null && (updateProfile || (legacyContactLines.length > 0 && legacyContactsReviewed) || JSON.stringify(identity) !== JSON.stringify(baseline))} busy={busy || uploading} ready={baseline !== null && (!legacyContactLines.length || legacyContactsReviewed)}
    submitLabel="Save details" onSubmit={() => onSubmit(updateProfile)} onClose={onClose} restoreFocusRef={restoreFocusRef}>
    {status}
    {legacyContactLines.length ? <div className="fld" role="group" aria-labelledby={`${id}-legacy-title`}>
      <strong id={`${id}-legacy-title`}>Review saved contact details</strong>
      <p className="fld-hint">These lines were found inside the old address. They have not been put into Address line 2. Enter the phone in Phone number if appropriate. Business email has no document-display field; retain any email elsewhere before saving this correction.</p>
      <ul>{legacyContactLines.map((line,index)=><li key={index} style={{overflowWrap:"anywhere"}}>{line}</li>)}</ul>
      <Checkbox label="I have reviewed these contact details and the corrected address" disabled={disabled} checked={legacyContactsReviewed} onChange={event=>setLegacyContactsReviewed(event.target.checked)}/>
    </div> : null}
    {error ? <RequestNotice title="Action not completed" message={error} /> : null}
    <DialogUpload value={identity.logoDataUrl} disabled={disabled} accept="image/png" maxBytes={150000} hint="PNG, up to 150 KB"
      onChoose={onChooseLogo} onRemove={() => onChange({ ...identity, logoDataUrl: null })} onBusyChange={setUploading}/>
    <div className="fld">
      <label className="fld-label" htmlFor={`${id}-name`}>Business name</label>
      <input className="input" id={`${id}-name`} name="name" autoComplete="organization" required maxLength={200}
        data-msg-required="Add the name clients will see on invoices." aria-describedby={`${id}-name-error`}
        disabled={disabled} value={identity.name} onChange={event => onChange({ ...identity, name: event.target.value })}/>
      <DialogError id={`${id}-name-error`}/>
    </div>
    <div className="fld">
      <label className="fld-label" htmlFor={`${id}-phone`}>Phone number</label>
      <input className="input" id={`${id}-phone`} name="phone" type="tel" autoComplete="tel" maxLength={100}
        disabled={disabled} value={identity.phone ?? ""} onChange={event => onChange({ ...identity, phone: event.target.value })}/>
    </div>
    {scope === "profile" ? <div className="fld"><label className="fld-label" htmlFor={`${id}-website`}>Website (optional)</label><input className="input" id={`${id}-website`} name="website" maxLength={200} disabled={disabled} value={identity.website??""} onChange={event=>onChange({...identity,website:event.target.value})}/></div> : null}
    <div className="fld">
      <label className="fld-label" htmlFor={`${id}-country`}>Country</label>
      <select className="input" id={`${id}-country`} name="country" autoComplete="country" required
        disabled={disabled} value={identity.address.country ?? ""} aria-describedby={`${id}-country-error`}
        data-msg-required="Select a country." onChange={event => onChange({ ...identity, address: { ...identity.address, country: event.target.value } })}>
        <option value="">Select country</option>
        {countries.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
      </select>
      <DialogError id={`${id}-country-error`}/>
    </div>
    {addressFields.map(([key, label, autoComplete, required, maxLength]) => key === "line2" && isUnitedStates
      && !showAddressLine2 && !identity.address.line2 ? <div className="fld" key={key}>
        <Button appearance="dialog" variant="quiet" disabled={disabled} aria-expanded={false}
          aria-controls={`${id}-line2`} onClick={() => setShowAddressLine2(true)}>+ Add address line 2</Button>
      </div> : <div className="fld" key={key}>
      <label className="fld-label" htmlFor={`${id}-${key}`}>{label}</label>
      <input ref={key === "line2" ? line2Ref : undefined} className="input" id={`${id}-${key}`} name={key} autoComplete={autoComplete} required={required} maxLength={maxLength}
        disabled={disabled} value={identity.address[key] ?? ""} aria-describedby={`${id}-${key}-error`}
        data-msg-required={`Add ${label.toLowerCase()}.`}
        onChange={event => onChange({ ...identity, address: { ...identity.address, [key]: event.target.value } })}/>
      <DialogError id={`${id}-${key}-error`}/>
    </div>)}
    {scope === "document" ? <div className="fld">
      <Checkbox label={`Show phone number on this ${kind}`} disabled={disabled} checked={identity.showPhone ?? false} onChange={event => onChange({ ...identity, showPhone: event.target.checked })}/>
      <Checkbox label={`Show address on this ${kind}`} disabled={disabled} checked={identity.showAddress !== false} onChange={event => onChange({ ...identity, showAddress: event.target.checked })}/>
    </div> : null}
    {scope === "document" ? <div className="fld">
      <Checkbox label="Also update Business Profile" disabled={disabled || !profileAvailable} checked={updateProfile} onChange={event => setUpdateProfile(event.target.checked)}/>
      {baseline && !profileAvailable ? <p className="fld-hint">Business Profile is temporarily unavailable. You can still save details to this invoice.</p> : null}
    </div> : null}
  </Container>;
}

export function readBusinessLogo(file: File): Promise<string> {
  if (file.type !== "image/png" || file.size > 150000) return Promise.reject(new Error("Choose a PNG logo smaller than 150 KB."));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("The logo could not be read."));
    reader.onerror = () => reject(new Error("The logo could not be read."));
    reader.readAsDataURL(file);
  });
}
